// Libraries
import React, { useContext, Fragment, useState } from 'react';

// Components
import Welcome from '../screens/Welcome';
import UserDetails from '../screens/UserDetails';
import Questionnaire from '../screens/Questionnaire';
import Results from '../screens/Results';
import Complete from '../screens/Complete';

// Services and contexts
import { sendEmail } from '../services/email';
import AlertContext from '../contexts/AlertContext';
import LanguageContext from '../contexts/LanguageContext';
import UserContext from '../contexts/UserContext';
import QuestionsContext from '../contexts/QuestionsContext';
import RatingsContext from '../contexts/RatingsContext';

// Material-UI
import Box from '@material-ui/core/Box';
import Stepper from '@material-ui/core/Stepper';
import Step from '@material-ui/core/Step';
import StepLabel from '@material-ui/core/StepLabel';
import Button from '@material-ui/core/Button';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

export default function DmaStepper() {
  const { throwAlert } = useContext(AlertContext);
  const { translate, language } = useContext(LanguageContext);
  const { questions } = useContext(QuestionsContext);
  const { ratings, calculateRatings } = useContext(RatingsContext);
  const { user } = useContext(UserContext);
  const userRatings = calculateRatings(questions);

  const [submitActive, setSubmitActive] = useState(true);
  const [activeStep, setActiveStep] = useState(0);
  const theme = useTheme();
  const breakPoint = useMediaQuery(theme.breakpoints.up('sm'));
  const steps = [
    {
      name: 'welcome',
      label: translate('UI_STEPPER_WELCOME')
    },
    {
      name: 'user',
      label: translate('UI_STEPPER_USER_DETAILS')
    },
    {
      name: 'questions',
      label: translate('UI_STEPPER_QUESTIONS')
    },
    {
      name: 'results',
      label: translate('UI_STEPPER_RESULTS')
    },
    {
      name: 'complete',
      label: translate('UI_STEPPER_COMPLETE')
    }
  ];

  const renderStepContent = (stepIndex) => {
    switch (stepIndex) {
      case 0:
        return <Welcome />;
      case 1:
        return <UserDetails />;
      case 2:
        return <Questionnaire />;
      case 3:
        return <Results />;
      case 5:
        return <Complete />;
      default:
        return '';
    }
  };

  const isUserFormValid = () => {
    const re = /\S+@\S+\.\S+/;

    const emailValid = user.email && re.test(user.email);
    const tncValid = user.tnc;

    return emailValid && tncValid;
  };

  const handleSubmit = async () => {
    setSubmitActive(false);
    const status = await sendEmail({ user, questions, ratings, userRatings, language });

    if (Math.max(...status) !== 200) {
      throwAlert(
        translate('UI_ALERT_THERE_WAS_AN_ERROR_WHILE_SUBMITTING_THE_RESULT'),
        'error',
        5000
      );
      setSubmitActive(true);
    } else setActiveStep((prevActiveStep) => prevActiveStep + 2);
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const renderBackButton = () => {
    if (activeStep > 0 && activeStep < steps.length - 1)
      return (
        <Box mr={2}>
          <Button variant="outlined" onClick={handleBack}>
            {translate('UI_BUTTON_BACK')}
          </Button>
        </Box>
      );
    return null;
  };

  const renderNextButton = () => {
    let disabled = false;

    // Disable next button on UserContact form until the inputs are all valid
    if (steps[activeStep].name === 'user') disabled = !isUserFormValid();

    if (steps[activeStep].name !== 'results' && steps[activeStep].name !== 'complete')
      return (
        <Box>
          <Button variant="contained" color="primary" onClick={handleNext} disabled={disabled}>
            {translate('UI_BUTTON_NEXT')}
          </Button>
        </Box>
      );
    return null;
  };

  const renderSubmitButton = () => {
    if (steps[activeStep].name === 'results')
      return (
        <Box>
          <Button
            variant="contained"
            disabled={!submitActive}
            color="primary"
            onClick={handleSubmit}>
            {translate('UI_BUTTON_SUBMIT')}
          </Button>
        </Box>
      );
    return null;
  };

  const renderButtons = () => {
    if (activeStep !== steps.length)
      return (
        <Box padding={3} display="flex" justifyContent="flex-end">
          {renderBackButton()}
          {renderNextButton()}
          {renderSubmitButton()}
        </Box>
      );
  };

  const renderStepLabels = () => {
    return steps.map((step) => (
      <Step key={step.label}>
        <StepLabel>{step.label}</StepLabel>
      </Step>
    ));
  };

  return (
    <Fragment>
      <Box margin={breakPoint ? 0 : 1}>
        <Stepper
          activeStep={activeStep}
          orientation={breakPoint ? 'horizontal' : 'vertical'}
          alternativeLabel={breakPoint ? true : false}>
          {renderStepLabels()}
        </Stepper>
      </Box>

      <Box>{renderStepContent(activeStep)}</Box>
      {renderButtons()}
    </Fragment>
  );
}
